import React, { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import ReactPlayer from 'react-player/lazy'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
} from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import Forward10RoundedIcon from '@mui/icons-material/Forward10Rounded'
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import Replay10RoundedIcon from '@mui/icons-material/Replay10Rounded'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import { useLocation, useNavigate, useParams } from 'react-router'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  BOOKING_SESSION_STATUS_MAP,
  EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import {
  getVcAuditRecordingPlayback,
  listVcAuditRecordings,
} from './vcAuditSessionsService'

void React

function formatDateTime(value) {
  if (!value) {
    return 'NA'
  }

  return dayjs(value).format('DD MMM YYYY, hh:mm A')
}

function formatDuration(totalSeconds) {
  const safeSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const seconds = safeSeconds % 60

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
      seconds,
    ).padStart(2, '0')}`
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function getEventTypeLabel(eventType) {
  return (
    EVENT_TYPES_ARRAY.find((option) => option?.id === eventType)?.title ||
    eventType ||
    'Unknown Event'
  )
}

function getSessionStatusLabel(sessionStatus) {
  return (
    BOOKING_SESSION_STATUS_MAP?.[sessionStatus]?.title ||
    sessionStatus ||
    'Unknown'
  )
}

function toSortTime(recording) {
  return dayjs(recording?.recordingStartedAt || recording?.recordingStoppedAt || 0).valueOf()
}

function buildRecordingLabel(recording) {
  if (!recording) {
    return 'Recording'
  }

  if (recording.recordingIndex !== null && recording.recordingIndex !== undefined) {
    return `Recording ${recording.recordingIndex + 1}`
  }

  return 'Recording'
}

export default function VcAuditRecordingPlayer() {
  const navigate = useNavigate()
  const location = useLocation()
  const { bookedEventId, recordingId } = useParams()
  const playerRef = useRef(null)
  const backPath = location.state?.from || '/support-home/booked-events'

  const [catalogState, setCatalogState] = useState({
    session: null,
    recordings: [],
  })
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState('')
  const [playbackState, setPlaybackState] = useState({
    session: null,
    recording: null,
    playback: null,
  })
  const [playbackLoading, setPlaybackLoading] = useState(false)
  const [playbackError, setPlaybackError] = useState('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [playedSeconds, setPlayedSeconds] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isSeeking, setIsSeeking] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)

  useEffect(() => {
    let active = true

    async function loadRecordingCatalog() {
      setCatalogLoading(true)
      setCatalogError('')

      const result = await listVcAuditRecordings(bookedEventId)

      if (!active) {
        return
      }

      if (!result.ok) {
        setCatalogState({
          session: null,
          recordings: [],
        })
        setCatalogError(result.message || 'Unable to load session recordings.')
        setCatalogLoading(false)
        return
      }

      setCatalogState({
        session: result.session || null,
        recordings: Array.isArray(result.recordings) ? result.recordings : [],
      })
      setCatalogLoading(false)
    }

    loadRecordingCatalog()

    return () => {
      active = false
    }
  }, [bookedEventId])

  const sortedRecordings = useMemo(() => {
    return [...(catalogState.recordings || [])].sort(
      (left, right) => toSortTime(right) - toSortTime(left),
    )
  }, [catalogState.recordings])

  const firstPlayableRecording = useMemo(() => {
    return sortedRecordings.find((recording) => recording?.hasPlayableAsset) || null
  }, [sortedRecordings])

  const selectedRecording = useMemo(() => {
    if (recordingId) {
      return sortedRecordings.find((recording) => recording?.id === recordingId) || null
    }

    return firstPlayableRecording || sortedRecordings[0] || null
  }, [firstPlayableRecording, recordingId, sortedRecordings])

  useEffect(() => {
    if (
      catalogLoading ||
      catalogError ||
      recordingId ||
      !selectedRecording?.id
    ) {
      return
    }

    navigate(
      `/support-home/booked-events/${bookedEventId}/recordings/${selectedRecording.id}`,
      {
        replace: true,
        state: {
          from: backPath,
        },
      },
    )
  }, [
    backPath,
    bookedEventId,
    catalogError,
    catalogLoading,
    navigate,
    recordingId,
    selectedRecording,
  ])

  useEffect(() => {
    let active = true

    async function loadPlayback() {
      if (catalogLoading) {
        setPlaybackLoading(false)
        return
      }

      if (catalogError) {
        setPlaybackState({
          session: null,
          recording: null,
          playback: null,
        })
        setPlaybackLoading(false)
        return
      }

      if (!selectedRecording?.id) {
        setPlaybackState({
          session: null,
          recording: null,
          playback: null,
        })
        setPlaybackLoading(false)
        return
      }

      if (!selectedRecording?.hasPlayableAsset) {
        setPlaybackState({
          session: null,
          recording: selectedRecording,
          playback: null,
        })
        setPlaybackError('This recording exists, but a playable media asset is not available yet.')
        setPlaybackLoading(false)
        return
      }

      setPlaybackLoading(true)
      setPlaybackError('')
      setPlaybackState({
        session: null,
        recording: selectedRecording,
        playback: null,
      })

      const result = await getVcAuditRecordingPlayback(
        bookedEventId,
        selectedRecording.id,
      )

      if (!active) {
        return
      }

      if (!result.ok) {
        setPlaybackState({
          session: null,
          recording: selectedRecording,
          playback: null,
        })
        setPlaybackError(result.message || 'Unable to load recording playback.')
        setPlaybackLoading(false)
        return
      }

      setPlaybackState({
        session: result.session || null,
        recording: result.recording || selectedRecording,
        playback: result.playback || null,
      })
      setPlaybackLoading(false)
    }

    loadPlayback()

    return () => {
      active = false
    }
  }, [bookedEventId, catalogError, catalogLoading, selectedRecording])

  useEffect(() => {
    setIsPlaying(false)
    setPlayedSeconds(0)
    setDuration(0)
    setIsSeeking(false)
    setPlaybackRate(1)
  }, [playbackState.playback?.recordingId])

  const session = playbackState.session || catalogState.session || null
  const bookedEvent = session?.bookedEvent || {}
  const clinic = session?.clinic || {}
  const mentor = session?.mentor || {}
  const patient = session?.patient || {}
  const canViewLive = session?.canViewLive === true && session?.isLive === true
  const currentRecording = playbackState.recording || selectedRecording || null
  const playback = playbackState.playback || null

  const handleSeekChange = (_, nextValue) => {
    const safeValue = Array.isArray(nextValue) ? nextValue[0] : nextValue
    setIsSeeking(true)
    setPlayedSeconds(safeValue)
  }

  const handleSeekCommitted = (_, nextValue) => {
    const safeValue = Array.isArray(nextValue) ? nextValue[0] : nextValue
    playerRef.current?.seekTo(safeValue, 'seconds')
    setPlayedSeconds(safeValue)
    setIsSeeking(false)
  }

  const seekBy = (deltaSeconds) => {
    const nextValue = Math.max(0, Math.min(duration || 0, playedSeconds + deltaSeconds))
    playerRef.current?.seekTo(nextValue, 'seconds')
    setPlayedSeconds(nextValue)
  }

  const handleSelectRecording = (nextRecordingId) => {
    if (!nextRecordingId || nextRecordingId === recordingId) {
      return
    }

    navigate(
      `/support-home/booked-events/${bookedEventId}/recordings/${nextRecordingId}`,
      {
        state: {
          from: backPath,
        },
      },
    )
  }

  const playerMessage = useMemo(() => {
    if (catalogError) {
      return catalogError
    }

    if (!catalogLoading && sortedRecordings.length === 0) {
      return 'No recordings are available for this booked event yet.'
    }

    if (!catalogLoading && recordingId && !selectedRecording) {
      return 'The selected recording could not be found for this session.'
    }

    if (playbackError) {
      return playbackError
    }

    if (!playbackLoading && currentRecording?.hasPlayableAsset && !playback?.playbackUrl) {
      return 'The playback asset is not available right now.'
    }

    return ''
  }, [
    catalogError,
    catalogLoading,
    currentRecording?.hasPlayableAsset,
    playback?.playbackUrl,
    playbackError,
    playbackLoading,
    recordingId,
    selectedRecording,
    sortedRecordings.length,
  ])

  return (
    <>
      <SupportHeader
        heading="Recording Playback"
        subHeading={bookedEvent?.eventName || getEventTypeLabel(bookedEvent?.eventType)}
        showBackButton
        backPath={backPath}
      />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', lg: 'center' }}
            >
              <Box>
                <Typography variant="h6">
                  {bookedEvent?.eventName || getEventTypeLabel(bookedEvent?.eventType)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(bookedEvent?.startTime)}
                  {bookedEvent?.endTime
                    ? ` to ${formatDateTime(bookedEvent?.endTime)}`
                    : ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={getEventTypeLabel(bookedEvent?.eventType)} />
                <Chip
                  label={getSessionStatusLabel(bookedEvent?.sessionStatus)}
                  variant="outlined"
                />
                <Chip
                  icon={<VideocamOutlinedIcon />}
                  label={`${sortedRecordings.length} recording${sortedRecordings.length === 1 ? '' : 's'}`}
                  variant="outlined"
                />
                {session?.isLive ? (
                  <Chip
                    color="error"
                    icon={<FiberManualRecordIcon />}
                    label={session?.liveBadgeLabel || 'Live'}
                  />
                ) : null}
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Clinic
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {clinic?.clinicBrandName || 'Unknown Clinic'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clinic?.clinicLocationName || 'Unknown Location'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Practitioner
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {mentor?.name || 'Unknown Practitioner'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Patient
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {patient?.name || patient?.patientNumber || 'Unknown Patient'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Current recording
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {buildRecordingLabel(currentRecording)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentRecording?.recordingProvider || 'Unknown provider'}
                </Typography>
              </Box>
            </Box>

            {canViewLive ? (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  startIcon={<FiberManualRecordIcon color="error" />}
                  onClick={() =>
                    navigate(`/support-home/booked-events/${bookedEventId}/live`, {
                      state: {
                        from: backPath,
                      },
                    })
                  }
                >
                  Switch to live viewer
                </Button>
              </Box>
            ) : null}
          </CardContent>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.9fr) minmax(320px, 0.9fr)' },
            gap: 2.5,
            alignItems: 'stretch',
          }}
        >
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                background:
                  'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(17,24,39,0.96) 100%)',
                minHeight: { xs: 280, md: 520 },
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'stretch',
              }}
            >
              {catalogLoading || playbackLoading ? (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: 280, md: 520 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={32} sx={{ color: 'white' }} />
                </Box>
              ) : playerMessage ? (
                <Box
                  sx={{
                    width: '100%',
                    minHeight: { xs: 280, md: 520 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 3,
                  }}
                >
                  <Alert
                    severity={
                      catalogError || playbackError || (recordingId && !selectedRecording)
                        ? 'error'
                        : 'info'
                    }
                    sx={{ width: '100%', maxWidth: 720 }}
                  >
                    {playerMessage}
                  </Alert>
                  {!currentRecording?.hasPlayableAsset && firstPlayableRecording?.id ? (
                    <Button
                      variant="contained"
                      onClick={() => handleSelectRecording(firstPlayableRecording.id)}
                    >
                      Open a playable recording
                    </Button>
                  ) : null}
                </Box>
              ) : (
                <ReactPlayer
                  key={playback?.recordingId || playback?.playbackUrl}
                  ref={playerRef}
                  url={playback?.playbackUrl}
                  width="100%"
                  height="100%"
                  playing={isPlaying}
                  playbackRate={playbackRate}
                  controls={false}
                  onDuration={(nextDuration) => setDuration(nextDuration)}
                  onProgress={({ playedSeconds: nextPlayedSeconds }) => {
                    if (!isSeeking) {
                      setPlayedSeconds(nextPlayedSeconds)
                    }
                  }}
                  onEnded={() => {
                    setIsPlaying(false)
                    setPlayedSeconds(duration)
                  }}
                />
              )}
            </Box>

            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="h6">
                      {buildRecordingLabel(currentRecording)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Started {formatDateTime(currentRecording?.recordingStartedAt)}
                      {currentRecording?.recordingStoppedAt
                        ? ` • Stopped ${formatDateTime(currentRecording?.recordingStoppedAt)}`
                        : ''}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {currentRecording?.recordingState ? (
                      <Chip
                        size="small"
                        label={currentRecording.recordingState}
                        variant="outlined"
                      />
                    ) : null}
                    {playback?.playbackType ? (
                      <Chip size="small" label={playback.playbackType.toUpperCase()} />
                    ) : null}
                    {playback?.mimeType ? (
                      <Chip size="small" label={playback.mimeType} variant="outlined" />
                    ) : null}
                  </Stack>
                </Stack>

                <Slider
                  min={0}
                  max={duration || 0}
                  step={1}
                  value={Math.min(playedSeconds, duration || 0)}
                  onChange={handleSeekChange}
                  onChangeCommitted={handleSeekCommitted}
                  disabled={!playback?.playbackUrl}
                />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      aria-label="Replay 10 seconds"
                      onClick={() => seekBy(-10)}
                      disabled={!playback?.playbackUrl}
                    >
                      <Replay10RoundedIcon />
                    </IconButton>
                    <IconButton
                      aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
                      color="primary"
                      onClick={() => setIsPlaying((currentValue) => !currentValue)}
                      disabled={!playback?.playbackUrl}
                    >
                      {isPlaying ? (
                        <PauseCircleFilledRoundedIcon sx={{ fontSize: 38 }} />
                      ) : (
                        <PlayCircleFilledRoundedIcon sx={{ fontSize: 38 }} />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="Forward 10 seconds"
                      onClick={() => seekBy(10)}
                      disabled={!playback?.playbackUrl}
                    >
                      <Forward10RoundedIcon />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {formatDuration(playedSeconds)} / {formatDuration(duration)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Speed
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 92 }}>
                      <Select
                        value={playbackRate}
                        onChange={(event) => setPlaybackRate(Number(event.target.value))}
                        disabled={!playback?.playbackUrl}
                      >
                        {[0.75, 1, 1.25, 1.5, 2].map((speedOption) => (
                          <MenuItem key={speedOption} value={speedOption}>
                            {speedOption}x
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              height: 'fit-content',
            }}
          >
            <CardContent>
              <Typography variant="h6">Session recordings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Review available recording attempts for this booked event.
              </Typography>

              <Divider sx={{ my: 2 }} />

              {catalogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : sortedRecordings.length === 0 ? (
                <Alert severity="info">No recordings have been stored for this session yet.</Alert>
              ) : (
                <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {sortedRecordings.map((recording) => {
                    const isActive = recording?.id === currentRecording?.id
                    const isLatest = recording?.id === session?.latestRecording?.id

                    return (
                      <ListItemButton
                        key={recording?.id}
                        selected={isActive}
                        onClick={() => handleSelectRecording(recording?.id)}
                        sx={{
                          border: '1px solid',
                          borderColor: isActive ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          alignItems: 'flex-start',
                          px: 1.5,
                          py: 1.25,
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            component: 'div',
                          }}
                          secondaryTypographyProps={{
                            component: 'div',
                          }}
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography variant="subtitle2">
                                {buildRecordingLabel(recording)}
                              </Typography>
                              {isLatest ? (
                                <Chip size="small" label="Latest" color="primary" />
                              ) : null}
                              {!recording?.hasPlayableAsset ? (
                                <Chip size="small" label="Not playable" variant="outlined" />
                              ) : null}
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatDateTime(recording?.recordingStartedAt)}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {recording?.recordingState ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={recording.recordingState}
                                  />
                                ) : null}
                                {recording?.recordingProvider ? (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={recording.recordingProvider}
                                  />
                                ) : null}
                                {recording?.preferredPlaybackType ? (
                                  <Chip
                                    size="small"
                                    label={recording.preferredPlaybackType.toUpperCase()}
                                  />
                                ) : null}
                              </Stack>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    )
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  )
}
